import { Button } from "@/modules/shadcn/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/modules/shadcn/ui/dropdown-menu";
import { EvalActionTypeEnum, type EvalAction } from "@repo/schema";
import { PlusIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { actionDescriptors, descriptorFor } from "./actionRegistry";

type AddActionMenuProps = {
  actions: EvalAction[];
  /** Receives the next list; the caller owns writing it into the form. */
  onAdd: (next: EvalAction[], addedIndex: number) => void;
};

export function AddActionMenu({ actions, onAdd }: AddActionMenuProps) {
  const { t } = useTranslation(["evaluation", "common"]);
  const handlePick = (type: EvalActionTypeEnum) => {
    const descriptor = descriptorFor(type);
    if (!descriptor) return;

    // `replaces` makes alert-vs-warning a one-click swap instead of a dead end
    // the user has to clear by hand first.
    const kept = descriptor.replaces?.length
      ? actions.filter((action) => !descriptor.replaces!.includes(action.type))
      : actions;

    const next = [...kept, descriptor.createDefault(kept)];
    onAdd(next, next.length - 1);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto gap-1 p-0 text-xs"
          />
        }
      >
        <PlusIcon />
        {t("actions.add")}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {actionDescriptors.map(
          ({ type, label, description, Icon, replaces, unavailableReason }) => {
            const reasonKey = unavailableReason?.(actions) ?? null;
            const reason = reasonKey && t(reasonKey);
            const displaced = replaces?.filter((replaced) =>
              actions.some((action) => action.type === replaced),
            );
            const alreadyPresent =
              !unavailableReason && actions.some((a) => a.type === type);

            return (
              <DropdownMenuItem
                key={type}
                className="items-start"
                disabled={!!reasonKey || alreadyPresent}
                onClick={() => handlePick(type)}
              >
                <Icon className="mt-0.5 size-4 shrink-0" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm">{t(label)}</span>
                  <span className="text-xs text-muted-foreground">
                    {reason ??
                      (alreadyPresent
                        ? t("actions.alreadyAdded")
                        : displaced?.length
                          ? t("actions.replaces", {
                              types: displaced
                                .map((d) => {
                                  const replaced = descriptorFor(d);
                                  return replaced ? t(replaced.label) : d;
                                })
                                .join(", "),
                            })
                          : t(description))}
                  </span>
                </div>
              </DropdownMenuItem>
            );
          },
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
